// =============================================================================
// Product Tour - Overlay spotlight pour guider les nouveaux utilisateurs
// Se déclenche à la première connexion (persisté en base) et relançable depuis la sidebar
// =============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ArrowRight, ArrowLeft, Sparkles, Search, CalendarDays, Users, Bell, Building2, UserPlus, LayoutGrid, FolderKanban, FileText, MessageSquareText, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  markProductTourCompleted,
} from '@/modules/core/data/onboardingRepository';
import { useAuth } from '@/contexts/AuthContext';
import aiIcon from '@/assets/icone-ai.png';

export interface TourStep {
  selector: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  navigateTo?: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    selector: '[data-tour="topbar"]',
    title: 'Barre supérieure',
    description: 'Accédez à votre workspace, la recherche rapide (⌘K), les notifications et votre profil.',
    icon: <Search className="h-5 w-5" />,
    position: 'bottom',
  },
  {
    selector: '[data-tour="modules-sidebar"]',
    title: 'Modules de navigation',
    description: 'Naviguez entre les modules : Tableau de bord, Projets, Planning, IA, Équipes, Documents, Wiki, Discussion.',
    icon: <LayoutGrid className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="module-projects"]',
    title: 'Module Projets',
    description: 'Le cœur de Lapyno. Gérez vos projets, tâches, jalons, budgets et feuilles de temps.',
    icon: <FolderKanban className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="module-planning"]',
    title: 'Module Planning',
    description: 'Visualisez la timeline de votre équipe, la charge de travail et la disponibilité sur tous les projets.',
    icon: <CalendarDays className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="module-ai"]',
    title: 'Module IA',
    description: 'L\'intelligence artificielle de Lapyno : chat IA, agents autonomes, résumés automatiques et suggestions.',
    icon: <Sparkles className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="module-teams"]',
    title: 'Module Équipes',
    description: 'Gérez vos équipes, consultez les profils et analysez la performance de chacun.',
    icon: <Users className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="module-documents"]',
    title: 'Module Documents',
    description: 'Créez et partagez des documents collaboratifs avec votre équipe, ajoutez des labels et des membres.',
    icon: <FileText className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="module-wiki"]',
    title: 'Module Wiki',
    description: 'Construisez votre base de connaissances : documentez les processus, guides et ressources de votre équipe.',
    icon: <BookOpen className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="module-discussion"]',
    title: 'Module Discussion',
    description: 'Communiquez en temps réel avec votre équipe via des canaux organisés par projet, espace ou workspace.',
    icon: <MessageSquareText className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="module-admin"]',
    title: 'Workspace & Paramètres',
    description: 'Gérez les membres, les rôles, les permissions, les invitations et les paramètres de votre workspace.',
    icon: <Building2 className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="invite-member"]',
    title: 'Inviter des membres',
    description: 'Ajoutez des collaborateurs à votre workspace pour travailler ensemble sur vos projets.',
    icon: <UserPlus className="h-5 w-5" />,
    position: 'right',
  },
  {
    selector: '[data-tour="notifications"]',
    title: 'Notifications',
    description: 'Restez informé des mises à jour, mentions et changements sur vos projets en temps réel.',
    icon: <Bell className="h-5 w-5" />,
    position: 'bottom',
  },
  {
    selector: '[data-tour="tour-restart"]',
    title: 'Relancer le guide',
    description: 'Vous pouvez relancer ce guide à tout moment en cliquant sur cette icône ✨.',
    icon: <Sparkles className="h-5 w-5" />,
    position: 'right',
  },
];

const LOCAL_TOUR_KEY_PREFIX = 'lapyno_product_tour_completed';
const ACTIVE_TOUR_SESSION_KEY_PREFIX = 'lapyno_product_tour_session';
const LOCAL_TOUR_ELIGIBLE_KEY_PREFIX = 'lapyno_product_tour_eligible';

// Namespace storage keys per user to avoid cross-user pollution when several
// accounts share the same browser (e.g. dev/staging admin + invited user).
const localTourKey = (userId: string) => `${LOCAL_TOUR_KEY_PREFIX}:${userId}`;
const activeTourSessionKey = (userId: string) => `${ACTIVE_TOUR_SESSION_KEY_PREFIX}:${userId}`;
const localTourEligibleKey = (userId: string) => `${LOCAL_TOUR_ELIGIBLE_KEY_PREFIX}:${userId}`;

type ActiveTourSession = {
  isOpen: boolean;
  currentStep: number;
};

function getActiveTourSession(userId: string | undefined): ActiveTourSession {
  if (typeof window === 'undefined' || !userId) {
    return { isOpen: false, currentStep: 0 };
  }

  try {
    const raw = sessionStorage.getItem(activeTourSessionKey(userId));
    if (!raw) return { isOpen: false, currentStep: 0 };

    const parsed = JSON.parse(raw) as Partial<ActiveTourSession>;
    const currentStep = Number.isInteger(parsed.currentStep)
      ? Math.max(0, Math.min(parsed.currentStep as number, TOUR_STEPS.length - 1))
      : 0;

    return {
      isOpen: parsed.isOpen === true,
      currentStep,
    };
  } catch {
    return { isOpen: false, currentStep: 0 };
  }
}

function writeActiveTourSession(userId: string | undefined, session: ActiveTourSession) {
  if (typeof window === 'undefined' || !userId) return;
  sessionStorage.setItem(activeTourSessionKey(userId), JSON.stringify(session));
}

function clearActiveTourSession(userId: string | undefined) {
  if (typeof window === 'undefined' || !userId) return;
  sessionStorage.removeItem(activeTourSessionKey(userId));
}

/** Mark tour as completed in DB + localStorage */
async function markTourCompleted(userId: string) {
  localStorage.setItem(localTourKey(userId), 'true');
  try {
    await markProductTourCompleted(userId);
  } catch {
    // Non-blocking: localStorage already prevents the tour from reopening.
  }
}

/** Check local tour completion only to avoid noisy RLS errors on every page load. */
function isTourCompleted(userId: string): boolean {
  return localStorage.getItem(localTourKey(userId)) === 'true';
}

interface ProductTourProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

export function ProductTour({ forceOpen = false, onClose }: ProductTourProps) {
  const { user } = useAuth();
  const location = useLocation();
  const restoredTourSessionRef = useRef<ActiveTourSession>(getActiveTourSession(user?.id));
  const [isOpen, setIsOpen] = useState(restoredTourSessionRef.current.isOpen);
  const [currentStep, setCurrentStep] = useState(restoredTourSessionRef.current.currentStep);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Check if tour should show
  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
      setCurrentStep(0);
      writeActiveTourSession(user?.id, { isOpen: true, currentStep: 0 });
      return;
    }

    if (!user || isOpen) return;

    const restoredSession = getActiveTourSession(user.id);
    if (restoredSession.isOpen) {
      setIsOpen(true);
      setCurrentStep(restoredSession.currentStep);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      const completed = isTourCompleted(user.id);
      if (cancelled || completed) return;

      // Only auto-open the tour for users who actually went through the
      // onboarding wizard (i.e. they created their own workspace). Invited
      // members skip /onboarding entirely and must not see this auto-tour —
      // they can still launch it manually from the sidebar ✨ icon.
      const wentThroughWizard = localStorage.getItem(localTourEligibleKey(user.id)) === 'true';
      if (!cancelled && wentThroughWizard) {
        setIsOpen(true);
        setCurrentStep(0);
        writeActiveTourSession(user.id, { isOpen: true, currentStep: 0 });
      }
    }, 1500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [forceOpen, user, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    writeActiveTourSession(user?.id, { isOpen: true, currentStep });
  }, [isOpen, currentStep, user?.id]);

  // Update spotlight position
  const updateSpotlight = useCallback(() => {
    if (!isOpen) return;
    const step = TOUR_STEPS[currentStep];
    if (!step) return;

    const element = document.querySelector(step.selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      setSpotlightRect(rect);
    } else {
      setSpotlightRect(null);
    }
  }, [isOpen, currentStep]);

  // Re-run spotlight on step or route change
  useEffect(() => {
    updateSpotlight();
    const t1 = setTimeout(updateSpotlight, 300);
    window.addEventListener('resize', updateSpotlight);
    return () => {
      clearTimeout(t1);
      window.removeEventListener('resize', updateSpotlight);
    };
  }, [updateSpotlight]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    if (user) markTourCompleted(user.id);
    clearActiveTourSession(user?.id);
    setIsOpen(false);
    onClose?.();
  };

  const handleSkip = () => {
    if (user) markTourCompleted(user.id);
    clearActiveTourSession(user?.id);
    setIsOpen(false);
    onClose?.();
  };

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];
  const padding = 8;

  // Calculate tooltip position with viewport clamping
  const getTooltipStyle = (): React.CSSProperties => {
    if (!spotlightRect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const pos = step.position || 'right';
    const tooltipWidth = 340;
    const tooltipHeight = 200;
    const tooltipGap = 16;
    const margin = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top: number;
    let left: number;

    switch (pos) {
      case 'right':
        top = spotlightRect.top + spotlightRect.height / 2 - 80;
        left = spotlightRect.right + padding + tooltipGap;
        break;
      case 'left':
        top = spotlightRect.top + spotlightRect.height / 2 - 80;
        left = spotlightRect.left - padding - tooltipGap - tooltipWidth;
        break;
      case 'bottom':
        top = spotlightRect.bottom + padding + tooltipGap;
        left = spotlightRect.left + spotlightRect.width / 2 - tooltipWidth / 2;
        break;
      case 'top':
        top = spotlightRect.top - padding - tooltipGap - tooltipHeight;
        left = spotlightRect.left + spotlightRect.width / 2 - tooltipWidth / 2;
        break;
      default:
        top = spotlightRect.top;
        left = spotlightRect.right + padding + tooltipGap;
    }

    if (left + tooltipWidth > vw - margin) {
      left = spotlightRect.left - padding - tooltipGap - tooltipWidth;
    }
    if (left < margin) {
      left = spotlightRect.right + padding + tooltipGap;
    }
    left = Math.max(margin, Math.min(left, vw - tooltipWidth - margin));
    top = Math.max(margin, Math.min(top, vh - tooltipHeight - margin));

    return { top, left };
  };

  return (
    <div className="fixed inset-0 z-[9999]" onClick={handleSkip}>
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect
                x={spotlightRect.left - padding}
                y={spotlightRect.top - padding}
                width={spotlightRect.width + padding * 2}
                height={spotlightRect.height + padding * 2}
                rx="12"
                ry="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#tour-spotlight-mask)"
          style={{ pointerEvents: 'auto' }}
        />
      </svg>

      {/* Spotlight border glow */}
      {spotlightRect && (
        <div
          className="absolute rounded-xl border-2 border-primary/60 shadow-[0_0_30px_rgba(99,102,241,0.3)] transition-all duration-300 ease-out"
          style={{
            top: spotlightRect.top - padding,
            left: spotlightRect.left - padding,
            width: spotlightRect.width + padding * 2,
            height: spotlightRect.height + padding * 2,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="absolute w-[340px] bg-card border border-border rounded-xl shadow-2xl p-5 transition-all duration-300 ease-out animate-in fade-in-0 slide-in-from-bottom-2"
        style={{ ...getTooltipStyle(), pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Step indicator */}
        <div className="flex items-center gap-1.5 mb-3 pr-6">
          {TOUR_STEPS.map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === currentStep
                  ? 'w-6 bg-primary'
                  : i < currentStep
                    ? 'w-1.5 bg-primary/40'
                    : 'w-1.5 bg-muted-foreground/20'
              )}
            />
          ))}
          <span className="ml-auto text-xs text-muted-foreground">
            {currentStep + 1}/{TOUR_STEPS.length}
          </span>
        </div>

        {/* Content */}
        <div className="flex items-start gap-3 mb-4">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            {step.icon}
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm mb-1">{step.title}</h3>
            <p className="text-muted-foreground text-xs leading-relaxed">{step.description}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="onboarding-tour-skip"
          >
            Passer le tour
          </button>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <Button variant="ghost" size="sm" onClick={handlePrev} className="h-8 px-3 text-xs">
                <ArrowLeft className="h-3 w-3 mr-1" />
                Précédent
              </Button>
            )}
            <Button size="sm" onClick={handleNext} className="h-8 px-3 text-xs">
              {currentStep < TOUR_STEPS.length - 1 ? (
                <>
                  Suivant
                  <ArrowRight className="h-3 w-3 ml-1" />
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3 mr-1" />
                  Commencer
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to re-trigger the tour
export function useProductTour() {
  const [tourOpen, setTourOpen] = useState(false);

  const startTour = useCallback(() => {
    setTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setTourOpen(false);
  }, []);

  // Listen for custom event from sidebar
  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('lapyno:start-tour', handler);
    return () => window.removeEventListener('lapyno:start-tour', handler);
  }, [startTour]);

  return { tourOpen, startTour, closeTour };
}
